import * as React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink } from "react-router-dom";

import { getCategoryStyle } from "../utils/blogTaxonomy";

export const BLOG_FALLBACK_IMAGE = "/images/bannerImage.jpg";

export default function BlogPostCard({ post, fixedHeight }) {
  const {
    title,
    excerpt,
    date,
    image,
    id,
    slug,
    category,
    tags = [],
    author,
    author_role: authorRole,
    comments_count: commentsCount = 0,
  } = post || {};
  const categoryStyle = getCategoryStyle(category);
  const compact = Boolean(fixedHeight);
  const hasComments = Number(commentsCount) > 0;
  const compactImageHeight =
    typeof fixedHeight === "number" && fixedHeight >= 500
      ? Math.min(340, Math.round(fixedHeight * 0.58))
      : 270;

  return (
    <Card
      variant="outlined"
      sx={{
        height: fixedHeight || "100%",
        minHeight: compact ? 0 : 386,
        display: "flex",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "#fff",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/blog/${id || slug}`}
        sx={{ display: "flex", flexDirection: "column", alignItems: "stretch" }}
      >
        <Box sx={{ position: "relative", width: "100%" }}>
          <CardMedia
            component="img"
            image={image || BLOG_FALLBACK_IMAGE}
            alt={title || "Blog article"}
            sx={{
              width: "100%",
              height: compact ? compactImageHeight : 174,
              objectFit: "cover",
              bgcolor: "#e7f3dc",
            }}
          />
          {category && (
            <Chip
              label={category}
              size="small"
              sx={{
                position: "absolute",
                left: 16,
                bottom: 14,
                maxWidth: "calc(100% - 32px)",
                color: categoryStyle.color,
                bgcolor: categoryStyle.background,
                border: `1px solid ${categoryStyle.color}22`,
                fontWeight: 600,
                fontSize: compact ? 10 : 11,
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          )}
          {hasComments && (
            <Chip
              icon={<ChatBubbleOutlineRoundedIcon />}
              label={`${commentsCount}`}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                height: compact ? 24 : 28,
                bgcolor: "rgba(255, 255, 255, 0.94)",
                color: "#8a6500",
                border: "1px solid rgba(247, 185, 0, 0.55)",
                fontWeight: 700,
                fontSize: compact ? 10 : 12,
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.12)",
                "& .MuiChip-icon": {
                  ml: 0.75,
                  color: "#d99b00",
                  fontSize: compact ? 14 : 16,
                },
              }}
            />
          )}
        </Box>

        <CardContent
          sx={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            p: compact ? 1.5 : 2.75,
            "&:last-child": { pb: compact ? 1.5 : 2.5 },
          }}
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            color="text.secondary"
            sx={{ mb: compact ? 0.75 : 1.25 }}
          >
            <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: compact ? 10 : 12 }}>
              {date
                ? new Date(date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : ""}
            </Typography>
            {author && (
              <>
                <Typography sx={{ fontSize: compact ? 10 : 12 }}>•</Typography>
                <Typography sx={{ fontSize: compact ? 10 : 12 }}>
                  {author}
                </Typography>
              </>
            )}
            {authorRole && (
              <>
                <Typography sx={{ fontSize: compact ? 10 : 12 }}>•</Typography>
                <Typography
                  sx={{
                    fontSize: compact ? 10 : 12,
                    fontWeight: 600,
                    color: "#a87900",
                  }}
                >
                  {authorRole}
                </Typography>
              </>
            )}
          </Stack>

          <Typography
            sx={{
              fontSize: compact ? 14 : 17,
              lineHeight: 1.35,
              fontWeight: 600,
              mb: compact ? 0.5 : 1.25,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {title}
          </Typography>

          {excerpt && (
            <Typography
              color="text.secondary"
              sx={{
                fontSize: compact ? 11 : 13,
                lineHeight: compact ? 1.45 : 1.6,
                mb: compact ? 1 : 2,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
                overflow: "hidden",
              }}
            >
              {excerpt}
            </Typography>
          )}

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            spacing={1}
            sx={{ mt: "auto", width: "100%" }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ minWidth: 0 }}
            >
              {tags.slice(0, compact ? 2 : 3).map((tag) => (
                <Chip
                  key={tag}
                  icon={<LocalOfferOutlinedIcon />}
                  label={tag}
                  size="small"
                  sx={{
                    height: 23,
                    bgcolor: "#f7f7f7",
                    color: "text.secondary",
                    fontSize: 10,
                    "& .MuiChip-icon": { ml: 0.5, fontSize: 12 },
                  }}
                />
              ))}
            </Stack>
            {!compact && (
              <Stack
                direction="row"
                spacing={0.4}
                alignItems="center"
                sx={{ flexShrink: 0, color: "#e9a700" }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                  Read
                </Typography>
                <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
